import { z } from 'zod';

/**
 * Moderation forms (doc 09 §4.7).
 *
 * Only `notesSchema` drives a React-Hook-Form (InternalNotesCard). The reason / message inputs for
 * destructive + side-effect actions are captured through the shared `useConfirm({ requireReason })`
 * textarea, so they are validated at the call site rather than via RHF. The other schemas are kept as
 * the documented validation contract for whenever those flows move into dedicated forms.
 */

/** Irreversible actions require a non-blank reason (destructive confirm policy). */
export const reasonRequiredSchema = z.object({
  reason: z.string().trim().min(1, 'A reason is required'),
});

/** Reversible actions may carry an optional reason. */
export const reasonOptionalSchema = z.object({
  reason: z.string().trim().default(''),
});

/** Send-feedback-email body — backend enforces @NotBlank / @minLength 1. */
export const emailSchema = z.object({
  message: z.string().trim().min(1, 'Message must not be blank'),
});

/** Solve-feedback note — dropped server-side today (doc 09 §8, risk #3), kept for local UX. */
export const solveSchema = z.object({
  note: z.string().trim().default(''),
});

/** Internal note — only saved when non-blank (parity ModerationDetailsViewModel.kt:404). */
export const notesSchema = z.object({
  notes: z.string().trim().min(1, 'Note must not be blank'),
});

export type NotesForm = z.infer<typeof notesSchema>;

/** Send Notification dialog — real POST /admin/api/moderation/send-notification body. */
export const sendNotificationSchema = z.object({
  title: z.string().trim().min(1, 'Title is required'),
  message: z.string().trim().min(1, 'Message is required'),
});

export type SendNotificationForm = z.infer<typeof sendNotificationSchema>;
